import { useEffect } from 'react';

interface StructuredDataProps {
  data: Record<string, any>;
  id?: string;
}

const StructuredData: React.FC<StructuredDataProps> = ({ data, id = 'structured-data' }) => {
  useEffect(() => {
    let script = document.getElementById(id) as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => {
      script?.remove();
    };
  }, [data, id]);

  return null;
};

// Pre-built structured data generators
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Genova AI Studio',
  url: 'https://genova-ai-studio.lovable.app',
  logo: 'https://storage.googleapis.com/gpt-engineer-file-uploads/gwD6XkgcCCcAWdnh4A0IrJQ5hxW2/uploads/1765115203704-1000014950.png',
  description: 'AI-powered content creation platform for generating images, voice, and scripts.',
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    availableLanguage: ['English', 'Hindi', 'Tamil'],
  },
};

export const softwareAppSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Genova AI Studio',
  applicationCategory: 'MultimediaApplication',
  operatingSystem: 'Web',
  url: 'https://genova-ai-studio.lovable.app',
  description: 'Create stunning AI-generated images, realistic voiceovers, and engaging scripts. The ultimate AI content creation platform.',
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '0',
    highPrice: '2499',
    priceCurrency: 'INR',
    offerCount: 4,
    offers: [
      {
        '@type': 'Offer',
        name: 'Free Plan',
        price: '0',
        priceCurrency: 'INR',
        description: '5 generation credits per month with standard quality',
      },
      {
        '@type': 'Offer',
        name: 'Starter Plan',
        price: '499',
        priceCurrency: 'INR',
        description: '20 images, 20 scripts, 20 min voice per month',
      },
      {
        '@type': 'Offer',
        name: 'Pro Plan',
        price: '1199',
        priceCurrency: 'INR',
        description: '40 images, 40 scripts, 40 min voice, HD quality, no watermark',
      },
      {
        '@type': 'Offer',
        name: 'Business Plan',
        price: '2499',
        priceCurrency: 'INR',
        description: '100 images, 100 scripts, 100 min voice, 4K Ultra HD',
      },
    ],
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '2500',
    bestRating: '5',
    worstRating: '1',
  },
  featureList: [
    'AI Image Generation',
    'AI Voice Synthesis',
    'AI Script Writing',
    'Multiple Art Styles',
    'Multi-language Voiceovers',
    'SEO-Optimized Content',
    'HD & 4K Quality Output',
    'No Watermark (Pro+)',
  ],
};

export const faqSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const reviewSchema = (reviews: { author: string; rating: number; body: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Genova AI Studio',
  description: 'AI-powered content creation platform',
  brand: { '@type': 'Brand', name: 'Genova AI Studio' },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: reviews.length.toString(),
    bestRating: '5',
    worstRating: '1',
  },
  review: reviews.map((r) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.author },
    reviewRating: { '@type': 'Rating', ratingValue: r.rating.toString(), bestRating: '5' },
    reviewBody: r.body,
  })),
});

export default StructuredData;
